import React, { useState, useEffect, useRef } from "react";
import {
  Key,
  Bell,
  Save,
  Eye,
  EyeOff,
  Check,
  X,
  User,
  Lock,
  Settings as SettingsIcon,
  Loader,
  Mail,
  AlertCircle,
  BarChart3,
  Sparkles,
  CheckCircle,
} from "lucide-react";
import { settingsAPI, authAPI, beaconsAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../common/PageHeader";
import Toggle from "../common/Toggle";

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const [activeTab, setActiveTab] = useState(urlParams.get("tab") || "user");
  const [showKeys, setShowKeys] = useState({});
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [apiKeys, setApiKeys] = useState({
    shodan_api_key: "",
  });

  const [notifications, setNotifications] = useState({
    slack_webhook_url: "",
    email_notifications: true,
    scan_completion_notifications: true,
    new_findings_notifications: true,
    trial_reminders: true,
    product_updates: true,
    weekly_summary: true,
  });

  const savedNotificationsRef = useRef(null);
  const [notificationsDirty, setNotificationsDirty] = useState(false);
  const [notificationsSaveSuccess, setNotificationsSaveSuccess] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);


  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      if (response.data.api_keys) {
        setApiKeys((prev) => ({ ...prev, ...response.data.api_keys }));
      }
      if (response.data.notifications) {
        const merged = {
          slack_webhook_url: "",
          email_notifications: true,
          scan_completion_notifications: true,
          new_findings_notifications: true,
          trial_reminders: true,
          product_updates: true,
          weekly_summary: true,
          ...response.data.notifications,
        };
        setNotifications(merged);
        savedNotificationsRef.current = merged;
      } else {
        savedNotificationsRef.current = {
          slack_webhook_url: "",
          email_notifications: true,
          scan_completion_notifications: true,
          new_findings_notifications: true,
          trial_reminders: true,
          product_updates: true,
          weekly_summary: true,
        };
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAPIKeyChange = (key, value) => {
    setApiKeys((prev) => ({ ...prev, [key]: value }));
  };

  const handleNotificationChange = (key, value) => {
    setNotifications((prev) => {
      const next = { ...prev, [key]: value };
      const saved = savedNotificationsRef.current;
      const isDirty = saved
        ? Object.keys(next).some((k) => next[k] !== saved[k])
        : true;
      setNotificationsDirty(isDirty);
      return next;
    });
  };

  const toggleShowKey = (key) => {
    setShowKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const saveAPIKeys = async () => {
    setSaving(true);
    setSuccess(null);
    setError(null);
    try {
      await settingsAPI.updateAPIKeys(apiKeys);
      setSuccess("API keys saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError("Failed to save API keys");
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async () => {
    setSaving(true);
    setSuccess(null);
    setError(null);
    setNotificationsSaveSuccess(false);
    try {
      await settingsAPI.updateNotifications(notifications);
      savedNotificationsRef.current = { ...notifications };
      setNotificationsDirty(false);
      setNotificationsSaveSuccess(true);
      setTimeout(() => setNotificationsSaveSuccess(false), 3000);
    } catch (err) {
      setError("Couldn't save preferences. Try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    setSaving(true);
    setSuccess(null);
    setError(null);

    // Validation
    if (
      !passwordData.current_password ||
      !passwordData.new_password ||
      !passwordData.confirm_password
    ) {
      setError("All password fields are required");
      setSaving(false);
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError("New passwords do not match");
      setSaving(false);
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setError("New password must be at least 8 characters");
      setSaving(false);
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      setSuccess("Password changed successfully!");
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to change password");
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const apiKeyFields = [
    {
      key: "shodan_api_key",
      label: "Shodan API Key",
      description: "Required for Shodan scans and vulnerability lookups",
    },
  ];




  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-shinobi-purple">Loading settings...</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Configure your API keys and preferences"
        icon={SettingsIcon}
      />
      <div className="p-8">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-shinobi-purple/20 border border-shinobi-purple text-shinobi-purple px-4 py-3 rounded flex items-center space-x-2">
            <Check className="w-5 h-5" />
            <span>{success}</span>
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-900/20 border border-danger-red text-danger-red px-4 py-3 rounded flex items-center space-x-2">
            <X className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-1 mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveTab("user")}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-all ${
              activeTab === "user"
                ? "text-shinobi-purple border-b-2 border-shinobi-purple"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <User className="w-5 h-5" />
            <span>User Settings</span>
          </button>
          <button
            onClick={() => setActiveTab("api-keys")}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-all ${
              activeTab === "api-keys"
                ? "text-shinobi-purple border-b-2 border-shinobi-purple"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Key className="w-5 h-5" />
            <span>API Keys</span>
          </button>
          <button
            onClick={() => setActiveTab("notifications")}
            className={`flex items-center space-x-2 px-6 py-3 font-semibold transition-all ${
              activeTab === "notifications"
                ? "text-shinobi-purple border-b-2 border-shinobi-purple"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Bell className="w-5 h-5" />
            <span>Notifications</span>
          </button>
        </div>

        {/* User Settings Tab */}
        {activeTab === "user" && (
          <div className="cyber-card rounded-lg p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center space-x-2">
              <Lock className="w-6 h-6" />
              <span>Change Password</span>
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              Update your password to keep your account secure. Password must be
              at least 8 characters.
            </p>

            <div className="space-y-6 max-w-xl">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) =>
                      setPasswordData((prev) => ({
                        ...prev,
                        current_password: e.target.value,
                      }))
                    }
                    className="terminal-input w-full px-4 py-3 pr-12 rounded focus:ring-2 focus:ring-shinobi-purple"
                    placeholder="Enter current password..."
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords((prev) => ({
                        ...prev,
                        current: !prev.current,
                      }))
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 transition-colors"
                  >
                    {showPasswords.current ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.new_password}
                    onChange={(e) =>
                      setPasswordData((prev) => ({
                        ...prev,
                        new_password: e.target.value,
                      }))
                    }
                    className="terminal-input w-full px-4 py-3 pr-12 rounded focus:ring-2 focus:ring-shinobi-purple"
                    placeholder="Enter new password..."
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords((prev) => ({ ...prev, new: !prev.new }))
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 transition-colors"
                  >
                    {showPasswords.new ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirm_password}
                    onChange={(e) =>
                      setPasswordData((prev) => ({
                        ...prev,
                        confirm_password: e.target.value,
                      }))
                    }
                    className="terminal-input w-full px-4 py-3 pr-12 rounded focus:ring-2 focus:ring-shinobi-purple"
                    placeholder="Confirm new password..."
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords((prev) => ({
                        ...prev,
                        confirm: !prev.confirm,
                      }))
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 transition-colors"
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={changePassword}
                disabled={saving}
                className="btn-cyber px-6 py-3 rounded flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Changing Password...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    <span>Change Password</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === "api-keys" && (
          <div className="cyber-card rounded-lg p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              External API Keys
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              Configure API keys for external services. Keys are encrypted and
              stored securely.
            </p>

            <div className="space-y-6">
              {apiKeyFields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    {field.label}
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {field.description}
                  </p>
                  <div className="relative">
                    <input
                      type={showKeys[field.key] ? "text" : "password"}
                      value={apiKeys[field.key] || ""}
                      onChange={(e) =>
                        handleAPIKeyChange(field.key, e.target.value)
                      }
                      className="terminal-input w-full px-4 py-3 pr-12 rounded focus:ring-2 focus:ring-shinobi-purple font-mono text-sm"
                      placeholder="Enter API key..."
                    />
                    <button
                      type="button"
                      onClick={() => toggleShowKey(field.key)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 transition-colors"
                    >
                      {showKeys[field.key] ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={saveAPIKeys}
                disabled={saving}
                className="btn-cyber px-6 py-3 rounded flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Save API Keys</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Billing Tab */}
        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div className="cyber-card rounded-lg p-6">
            {/* Section header */}
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 rounded-lg bg-shinobi-gradient flex items-center justify-center flex-shrink-0">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Email Notifications</h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  Choose which emails you'd like to receive from Wasabi Shinobi
                </p>
              </div>
            </div>

            {/* Per-section success banner */}
            {notificationsSaveSuccess && (
              <div className="mt-4 flex items-center space-x-2 px-4 py-3 rounded bg-shinobi-purple/10 border border-shinobi-purple/40 text-shinobi-purple text-sm">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span>Preferences saved.</span>
              </div>
            )}

            {/* Toggle rows */}
            <div className="mt-6 divide-y divide-slate-200">
              {/* Scan completion */}
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center space-x-4 flex-1 min-w-0 pr-6">
                  <div className="w-9 h-9 rounded-lg bg-cyber-200 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-neon-green" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">Scan Completion</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Get notified when a scan finishes — whether it succeeds or fails
                    </p>
                  </div>
                </div>
                <Toggle
                  checked={!!notifications.scan_completion_notifications}
                  onChange={(val) => handleNotificationChange("scan_completion_notifications", val)}
                  label="Scan completion notifications"
                />
              </div>

              {/* New findings */}
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center space-x-4 flex-1 min-w-0 pr-6">
                  <div className="w-9 h-9 rounded-lg bg-cyber-200 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-4 h-4 text-danger-red" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">New Findings</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Instant alert when HIGH or CRITICAL CVE findings are discovered
                    </p>
                  </div>
                </div>
                <Toggle
                  checked={!!notifications.new_findings_notifications}
                  onChange={(val) => handleNotificationChange("new_findings_notifications", val)}
                  label="New findings notifications"
                />
              </div>

              {/* Trial reminders */}
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center space-x-4 flex-1 min-w-0 pr-6">
                  <div className="w-9 h-9 rounded-lg bg-cyber-200 flex items-center justify-center flex-shrink-0">
                    <Bell className="w-4 h-4 text-shinobi-orange" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">Trial Reminders</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Expiry warnings on days 23, 28, and 30 of your trial — auto-enabled for trial accounts
                    </p>
                  </div>
                </div>
                <Toggle
                  checked={!!notifications.trial_reminders}
                  onChange={(val) => handleNotificationChange("trial_reminders", val)}
                  label="Trial reminder notifications"
                />
              </div>

              {/* Product updates */}
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center space-x-4 flex-1 min-w-0 pr-6">
                  <div className="w-9 h-9 rounded-lg bg-cyber-200 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-shinobi-purple" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">Product Updates</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Low-frequency announcements: new features, blog posts, major releases
                    </p>
                  </div>
                </div>
                <Toggle
                  checked={!!notifications.product_updates}
                  onChange={(val) => handleNotificationChange("product_updates", val)}
                  label="Product update notifications"
                />
              </div>

              {/* Weekly summary */}
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center space-x-4 flex-1 min-w-0 pr-6">
                  <div className="w-9 h-9 rounded-lg bg-cyber-200 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-4 h-4 text-neon-blue" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">Weekly Summary</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Monday morning digest: scan activity, finding counts, and beacon status from the past week
                    </p>
                  </div>
                </div>
                <Toggle
                  checked={!!notifications.weekly_summary}
                  onChange={(val) => handleNotificationChange("weekly_summary", val)}
                  label="Weekly summary notifications"
                />
              </div>
            </div>

            {/* Save button */}
            <div className="mt-6 flex items-center justify-between">
              {/* Inline error for this section */}
              {error && (
                <div className="flex items-center space-x-2 text-danger-red text-sm">
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {!error && <span />}
              <button
                onClick={saveNotifications}
                disabled={saving || !notificationsDirty}
                className={`btn-cyber px-6 py-3 rounded flex items-center space-x-2 transition-opacity ${
                  !notificationsDirty ? "opacity-40 cursor-not-allowed" : ""
                }`}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    <span>Save Preferences</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Settings;
